/**
 * @Author Valon.Ye
 * @Date 2022/12/29 10:35:22
 * @FilePath \commerce\src\models\order-status.ts
 * @Description
 */
import { useState, useCallback } from 'react'
import { getLocale } from 'umi'

import { useFetch } from '@/utils/hooks'
import { OrderStatusControllerApiParamCreator } from '@/api-auto-gen/orderManagement'
import type { OrderStatusResponse } from '@/api-auto-gen/orderManagement'

type IOrderStatusResponse = OrderStatusResponse & {
  keyName?: string
  enName?: string
  cnName?: string
}

type IOrderStatusOptions = IComboboxOption<string>
type IOrderStatusMap = Record<string, string>

const useOrderStatus = () => {
  const [statusOptions, setStatusOptions] = useState<IOrderStatusOptions[]>([])
  const [statusMap, setStatusMap] = useState<IOrderStatusMap>({})

  const { loading, run, data, error } = useFetch<BasicResponse<IOrderStatusResponse[]>>(
    OrderStatusControllerApiParamCreator.getAllOrderStatusUsingGET,
    {
      cacheKey: 'order-status',
      staleTime: -1,
      manual: true,
      onSuccess(res) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        updateOptions(res)
      },
      onError(statusError) {
        // 没有权限
        if (error?.code === '401101001') {
          return
        }
        statusError?.defaultErrorHandler?.()
      },
    },
  )

  const updateOptions = useCallback(
    (raw?: IOrderStatusResponse[]) => {
      const source = raw || (data as IOrderStatusResponse[]) || []
      const lang = getLocale()
      const { statusRecord, statusList } = source.reduce(
        (ret, item) => {
          const keyName = item.key_name || item.keyName
          const cnName = item.cn_name || item.cnName
          const enName = item.en_name || item.enName
          if (lang.startsWith('zh')) {
            // eslint-disable-next-line no-param-reassign
            ret.statusRecord[keyName!] = cnName!
            ret.statusList.push({
              id: keyName!,
              value: keyName!,
              label: cnName!,
            })
          }
          if (lang.startsWith('en')) {
            // eslint-disable-next-line no-param-reassign
            ret.statusRecord[keyName!] = enName!
            ret.statusList.push({
              id: keyName!,
              value: keyName!,
              label: enName!,
            })
          }
          return ret
        },
        {
          statusRecord: {} as IOrderStatusMap,
          statusList: [] as IOrderStatusOptions[],
        },
      )

      setStatusMap({ ...statusRecord })
      setStatusOptions([...statusList])
    },
    [data],
  )

  return {
    loading,
    orderStatusRequestError: error,
    refreshOrderStatusOptions: run,
    updateOptions,
    orderStatusMap: statusMap,
    orderStatusOptions: statusOptions,
  }
}

export default useOrderStatus
